import Cookies from "js-cookie";

const API_BASE = () => (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

export const isMediaMessageType = (type) =>
    ["image", "video", "document", "audio", "file"].includes(String(type || "").toLowerCase());

export const validateShareableMessage = (message) => {
    if (!message) return "No message selected to share";

    const shareType = message.shareType || message.original?.share_type || "";
    if (shareType === "sharetoview") return "View-once photos and videos cannot be shared";

    if (message.uploading) return "Wait until the upload finishes before sharing this file";

    const text = String(message.text || message.message || "").trim();
    const rawUrl =
        message.fileUrlRaw ||
        message.original?.file ||
        message.original?.file_url ||
        message.original?.file_path ||
        message.fileUrl ||
        message.file_url ||
        message.file_path;

    if (rawUrl && String(rawUrl).startsWith("blob:")) {
        return "This file is still uploading and cannot be shared yet";
    }

    const hasFile = Boolean(rawUrl) || isMediaMessageType(message.type);
    if (!text && !hasFile) return "This message has nothing to share";

    return null;
};

const resolveServerFilePath = (rawUrl) => {
    if (!rawUrl || String(rawUrl).startsWith("blob:")) return null;

    let path = String(rawUrl).trim();
    const apiBase = API_BASE();

    if (path.startsWith("http") && apiBase && path.startsWith(apiBase)) {
        path = path.slice(apiBase.length);
    }

    if (!path.startsWith("/") && !path.startsWith("http")) {
        path = `/${path}`;
    }

    return path;
};

export const getShareableFileMeta = (messageToShare) => {
    const validationError = validateShareableMessage(messageToShare);
    if (validationError && !isMediaMessageType(messageToShare?.type)) {
        throw new Error(validationError);
    }

    if (messageToShare?.uploading) {
        throw new Error("Wait until the upload finishes before sharing this file.");
    }

    const original = messageToShare?.original || {};
    const rawUrl =
        messageToShare?.fileUrlRaw ||
        original.file ||
        original.file_url ||
        original.file_path ||
        original.url ||
        messageToShare?.fileUrl ||
        messageToShare?.file_url ||
        messageToShare?.file_path;

    const serverPath = resolveServerFilePath(rawUrl);
    if (!serverPath) {
        throw new Error("This photo or video is not available to share yet.");
    }

    const apiBase = API_BASE();
    const downloadUrl = serverPath.startsWith("http") ? serverPath : `${apiBase}${serverPath}`;

    return {
        serverPath,
        downloadUrl,
        fileName:
            messageToShare?.fileName ||
            original.filename ||
            original.file_name ||
            original.name ||
            serverPath.split("/").pop() ||
            "file",
        mime:
            messageToShare?.mime ||
            original.mime_type ||
            original.mime ||
            original.file_mime ||
            undefined,
        shareType: messageToShare?.shareType || original.share_type || "",
    };
};

export const fetchShareFileBytes = async (downloadUrl) => {
    const token = Cookies.get("auth_token");
    const response = await fetch(downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
        throw new Error(`Could not load file for sharing (${response.status})`);
    }

    return new Uint8Array(await response.arrayBuffer());
};

export const shareMessageToContacts = async ({
    socket,
    messageToShare,
    targetContactIds = [],
    contactsList = [],
}) => {
    if (!messageToShare) throw new Error("No message to share");
    if (!Array.isArray(targetContactIds) || targetContactIds.length === 0) {
        throw new Error("Select at least one contact");
    }
    if (targetContactIds.length > 5) throw new Error("You can share to maximum 5 contacts");
    if (!socket?.connected) {
        throw new Error("Not connected to chat server. Please reconnect and try again.");
    }

    const validationError = validateShareableMessage(messageToShare);
    if (validationError) throw new Error(validationError);

    const text = String(messageToShare.text || messageToShare.message || "").trim();
    const isFileMessage =
        isMediaMessageType(messageToShare.type) ||
        Boolean(
            messageToShare.fileUrlRaw ||
                messageToShare.original?.file_url ||
                messageToShare.original?.file ||
                (messageToShare.fileUrl && !String(messageToShare.fileUrl).startsWith("blob:"))
        );

    let fileMeta = null;
    let fileBytes = null;

    if (isFileMessage) {
        fileMeta = getShareableFileMeta(messageToShare);
        fileBytes = await fetchShareFileBytes(fileMeta.downloadUrl);
    } else if (!text) {
        throw new Error("Message has no text or file to share");
    }

    const sendTextToOne = (toUserId, messageText) =>
        new Promise((resolve, reject) => {
            socket.emit(
                "send_message",
                {
                    to_user_id: toUserId,
                    message: messageText,
                    client_time: new Date().toISOString(),
                },
                (response) => {
                    if (response?.error) {
                        reject(new Error(response.error || "Failed to share message"));
                        return;
                    }
                    resolve(response);
                }
            );
        });

    const sendFileToOne = (toUserId, bytes, meta) =>
        new Promise((resolve, reject) => {
            socket.emit(
                "send_file",
                {
                    to_user_id: toUserId,
                    filename: meta.fileName,
                    mime_type: meta.mime,
                    caption: "",
                    file: bytes,
                    share_type: meta.shareType || "",
                    client_time: new Date().toISOString(),
                },
                (ack) => {
                    if (ack?.error) {
                        reject(new Error(ack.error || "Failed to share file"));
                        return;
                    }
                    resolve(ack);
                }
            );
        });

    for (const id of targetContactIds) {
        try {
            if (fileMeta && fileBytes) {
                await sendFileToOne(id, fileBytes, fileMeta);
            } else {
                await sendTextToOne(id, text);
            }
        } catch (err) {
            const targetName =
                contactsList.find((c) => String(c.user_id) === String(id))?.full_name ||
                contactsList.find((c) => String(c.user_id) === String(id))?.name ||
                contactsList.find((c) => String(c.user_id) === String(id))?.phone_number ||
                id;
            throw new Error(`Failed to share to ${targetName}: ${err.message}`);
        }
    }

    return true;
};
